/**
 * `ConnectorAiRow` — the four-state ladder for a conversational connector.
 *
 * LIFTED out of `StudioConfig.tsx` on 2026-08-11, unchanged, when `/agents`
 * became the second page that has to render it. The docblock below claims a
 * no-drift property — labels and per-state copy come from the connector's own
 * descriptor, so a connector that gains an AI ladder gains its row
 * automatically — and that property is VOID the moment a second copy of this
 * component exists. Hence a move rather than a duplicate; `StudioConfig.tsx`
 * imports it back.
 */

import type { JSX } from "react";
import {
  CONNECTOR_DESCRIPTORS,
  type AiAutonomyMode,
  type ConnectorCapability,
} from "@layer/canonical-data/connectors";
import type { ConnectorAiState } from "../lib/studio-config-patch";

/**
 * A conversational connector's row — four radio states instead of the usual
 * tick, because "is Layer allowed to touch this channel" and "may the
 * assistant answer without me" are separate questions and one checkbox could
 * only ask the first. A SECOND radio pair below the ladder carries the
 * capability axis — WHICH messages it engages with is the ladder; WHAT it may
 * do once engaged (answer only vs answer and act) is this pair.
 *
 * The labels and the per-state copy come from the connector's own
 * {@link ConnectorAiDescriptor} in `connectors.ts`, not from a table here, so
 * a connector that gains an AI ladder gains its row automatically — the same
 * no-drift rule the descriptor list already enforces for the plain rows.
 */
export function ConnectorAiRow({
  descriptor,
  enabled,
  autonomy,
  capability,
  onChange,
  onCapabilityChange,
}: {
  descriptor: (typeof CONNECTOR_DESCRIPTORS)[number];
  enabled: boolean;
  autonomy: AiAutonomyMode;
  capability: ConnectorCapability;
  onChange: (next: ConnectorAiState) => void;
  onCapabilityChange: (next: ConnectorCapability) => void;
}): JSX.Element {
  const ai = descriptor.ai;
  /* c8 ignore next — the caller only renders this row when `ai` is present. */
  if (ai === undefined) return <></>;
  const current: ConnectorAiState = enabled ? autonomy : "off";
  const options: ReadonlyArray<{
    value: ConnectorAiState;
    label: string;
    detail: string;
    danger?: boolean;
  }> = [
    {
      value: "off",
      label: `Off — ${descriptor.label} is paused`,
      detail: descriptor.pauses,
    },
    ...ai.options.map((o) => ({
      value: o.mode as ConnectorAiState,
      label: o.label,
      detail: o.detail,
      ...(o.danger === true ? { danger: true } : {}),
    })),
  ];
  const rowId = `connector-state-${descriptor.key}`;
  return (
    <div
      className="connector-row"
      style={{ padding: "8px 0", borderTop: "1px solid var(--c-line-94-c)" }}
    >
      <div style={{ fontWeight: 600 }}>
        {descriptor.label}
        {!enabled && (
          <span
            className="tag tag-danger"
            style={{ marginLeft: 8, fontSize: "0.8em" }}
          >
            paused
          </span>
        )}
        {enabled && current === "all" && (
          <span
            className="tag tag-danger"
            style={{ marginLeft: 8, fontSize: "0.8em" }}
          >
            answers everyone
          </span>
        )}
        {enabled && capability === "converse" && (
          <span className="tag" style={{ marginLeft: 8, fontSize: "0.8em" }}>
            answer only
          </span>
        )}
      </div>
      <div style={{ fontSize: "0.85em", color: "var(--c-ink-41)", margin: "2px 0 6px" }}>
        Unlike the other connectors this is not just on/off — choose how far the
        assistant may go on its own.
      </div>
      {options.map((opt) => (
        <label
          key={opt.value}
          htmlFor={`${rowId}-${opt.value}`}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            padding: "5px 0",
            cursor: "pointer",
          }}
        >
          <input
            type="radio"
            id={`${rowId}-${opt.value}`}
            name={rowId}
            value={opt.value}
            checked={current === opt.value}
            onChange={() => onChange(opt.value)}
            style={{ marginTop: 3 }}
          />
          <span style={{ flex: "1 1 auto" }}>
            <span style={{ fontWeight: 600 }}>{opt.label}</span>
            {opt.danger === true && current === opt.value && (
              <span
                className="tag tag-danger"
                style={{ marginLeft: 8, fontSize: "0.8em" }}
              >
                no intent check
              </span>
            )}
            <span
              style={{
                display: "block",
                fontSize: "0.85em",
                color: "var(--c-ink-41)",
                marginTop: 2,
              }}
            >
              {opt.detail}
            </span>
          </span>
        </label>
      ))}
      <div style={{ margin: "8px 0 2px", fontWeight: 600, fontSize: "0.9em" }}>
        What it may do when it answers
      </div>
      {ai.capabilityOptions.map((opt) => (
        <label
          key={opt.value}
          htmlFor={`${rowId}-capability-${opt.value}`}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            padding: "5px 0",
            cursor: "pointer",
          }}
        >
          <input
            type="radio"
            id={`${rowId}-capability-${opt.value}`}
            name={`${rowId}-capability`}
            value={opt.value}
            checked={capability === opt.value}
            onChange={() => onCapabilityChange(opt.value)}
            style={{ marginTop: 3 }}
          />
          <span style={{ flex: "1 1 auto" }}>
            <span style={{ fontWeight: 600 }}>{opt.label}</span>
            <span
              style={{
                display: "block",
                fontSize: "0.85em",
                color: "var(--c-ink-41)",
                marginTop: 2,
              }}
            >
              {opt.detail}
            </span>
          </span>
        </label>
      ))}
      <div style={{ fontSize: "0.8em", color: "var(--c-ink-41)", marginTop: 4 }}>
        This is the studio-wide default. Each member page carries a “
        {ai.memberSwitchLabel}” switch that overrules it for one person — on,
        so the assistant answers them even at “notify me only”, or off, which
        sticks and no setting here can undo. Pausing the connector outright
        wins over both. The “what it may do” choice above is studio-wide too:
        the member switch decides whether the assistant answers someone, never
        what it may do.
      </div>
    </div>
  );
}
